<template>
    <v-app class="ppv2-bg-white dark:ppv2-bg-dark">
        <v-container fluid>
            <CustomButtonSave @click="handleSaveAndExit" v-if="![1, 7].includes(step) && !isEditMode" :loading="loading" />
            <div
                v-if="isEditMode && step !== 7"
                :style="{
                    position: 'absolute',
                    top: '25px',
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    gap: '8px',
                    right: normalizeSize(50, true),
                    zIndex: 10,
                }"
                @click="redirectToList"
            >
                <ChevronLeft color="#505d92" :size="20" />
                <span class="ppv2-text-black ppv2-text-opacity-70" :style="{ fontSize: '16px' }">
                    {{ $t('experience.backToExperiences') }}
                </span>
            </div>
            <v-row class="ppv2-flex ppv2-h-full">
                <!-- Step headers on the left -->
                <v-col
                    v-if="step !== 7"
                    class="col col-md-2 col-sm-3 col-4 stepper-container"
                    :class="[{ 'stepper-container-edit !ppv2-pt-[40px]': id }, { 'mt-40': !id }]"
                >
                    <div
                        v-for="(s, index) in visibleSteps"
                        :key="s.id"
                        :class="[
                            'step-item',
                            {
                                disabled: (!id && s.id > highestValidatedStep) || s.id === 1,
                                completed: completedSteps.includes(s.id) && !id,
                                completedMenu: completedSteps.includes(s.id) && id,
                                active: step === s.id && !id,
                                activeMenu: step === s.id && id,
                            },
                            { '!ppv2-min-h-[44px] !ppv2-items-center': id },
                        ]"
                        @click="handleStepClick(s.id)"
                    >
                        <!-- Circle with checkmark or number -->
                        <div class="circle" v-if="!id">
                            <Check v-if="completedSteps.includes(s.id)" color="white" :size="14" />
                        </div>

                        <!-- Line between steps -->
                        <div
                            v-if="index < visibleSteps.length - 1 && !id"
                            class="line"
                            :class="{ completed: completedSteps.includes(s.id) }"
                        ></div>

                        <!-- Text -->
                        <div class="d-flex flex-column justify-content-center" :style="{ marginLeft: normalizeSize(22, true) }">
                            <div v-if="!id" class="step-title ppv2-text-black" :style="{ fontSize: sizes.stepTitle }">
                                ÉTAPE {{ getStepDisplayNumber(s.id) }}
                            </div>
                            <div
                                class="step-subtitle ppv2-text-black"
                                :class="{ '!ppv2-opacity-100': id }"
                                :style="{ fontSize: id ? sizes.subtitleMenu : sizes.subtitle }"
                            >
                                {{ s.subtitle }}
                            </div>
                        </div>
                    </div>
                </v-col>

                <!-- Step content on the right -->
                <v-col
                    :class="{
                        'col col-md-10 col-sm-9 col-8 step-content px-0 !ppv2-pt-[46px]': step !== 7,
                        'col-12': step === 7,
                    }"
                >
                    <v-stepper class="ppv2-bg-white dark:ppv2-bg-dark" v-model="step">
                        <v-stepper-content v-for="s in allSteps" :key="s.id" :step="s.id" class="mr-0 px-0 ppv2-h-full">
                            <transition name="fade-step" mode="out-in">
                                <div class="mb-4 ppv2-bg-white dark:ppv2-bg-dark" :key="s.id + '-' + step">
                                    <component
                                        :is="s.component"
                                        :ref="'step' + s.id"
                                        :step="s"
                                        :sizes="sizes"
                                        :id="isEditMode ? null : packageId"
                                        :is-creation-mode="!id"
                                        @next-step="handleNextStep(s.id)"
                                        @previous-step="goToPrevious(s.id - 1)"
                                        @save-and-exit="handleSaveAndExit"
                                    ></component>
                                </div>
                            </transition>
                        </v-stepper-content>
                    </v-stepper>
                </v-col>
            </v-row>
        </v-container>

        <!-- Confirm navigation dialog -->
        <CustomDialog
            v-model="showConfirmDialog"
            :defaultTitle="$t('experience.titleClosePopup')"
            customClassTitle="poppinsSemiBold"
            noScroll
        >
            <template #content>
                <p class="ppv2-text-center ppv2-text-black poppinsRegular" :style="{ fontSize: normalizeFontSize(15) }">
                    {{ $t('experience.descriptionClosePopup') }}
                </p>
            </template>
            <template #actions>
                <BaseButton
                    type="button"
                    variant="outline"
                    @click="_onConfirmDialog"
                    customHeight="43"
                    customClass="!ppv2-min-h-[40px]"
                >
                    {{ $t('experience.btnLeave') }}
                </BaseButton>
                <BaseButton
                    type="button"
                    variant="primary"
                    @click="_onCancelDialog"
                    customHeight="43"
                    customClass="!ppv2-min-h-[40px]"
                    :isLargeBtn="true"
                >
                    {{ $t('experience.btnStay') }}
                </BaseButton>
            </template>
        </CustomDialog>
    </v-app>
</template>

<script>
import Step1 from './components/Step1.vue'
import Step2 from './components/Step2.vue'
import Step3 from './components/Step3.vue'
import Step4 from './components/Step4.vue'
import Step5 from './components/Step5.vue'
import Step6 from './components/Step6.vue'
import Step8 from '../Experience/components/Step8.vue'
import { theme } from '../../../utils/theme'
import normalizeSize from '../../../utils/normalizeSize'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import CustomButtonSave from '../../Shared/CustomButtonSave.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import BaseButton from '../../Shared/BaseButton.vue'
import { Check, ChevronLeft } from 'lucide-vue'
import { mapGetters, mapActions } from 'vuex'
import { EventBus } from '../../../eventBus'

export default {
    name: 'PackageCreate',
    components: { CustomButtonSave, ChevronLeft, CustomDialog, BaseButton, Check },
    props: {
        id: {
            type: [Number, String],
            default: null,
        },
        country_vat_rates: {
            type: Array,
            default: () => [],
        },
        prestations_with_prices_times: {
            type: Array,
            default: () => [],
        },
        tags: {
            type: Array,
            default: () => [],
        },
        package_data: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            step: 2,
            completedSteps: [],
            highestValidatedStep: 1,
            isSaving: false,
            showConfirmDialog: false,
            _pendingConfirm: null,
            _pendingCancel: null,
            allSteps: [
                {
                    id: 1,
                    subtitle: this.$t('package.step1.subtitle'),
                    content: this.$t('package.step1.content'),
                    component: Step1,
                },
                {
                    id: 2,
                    subtitle: this.$t('package.step2.subtitle'),
                    content: this.$t('package.step2.content'),
                    component: Step2,
                },
                {
                    id: 3,
                    subtitle: this.$t('package.step3.subtitle'),
                    component: Step3,
                },
                {
                    id: 4,
                    subtitle: this.$t('package.step4.subtitle'),
                    component: Step4,
                },
                {
                    id: 5,
                    subtitle: this.$t('package.step5.subtitle'),
                    component: Step5,
                },
                {
                    id: 6,
                    subtitle: this.$t('package.step6.subtitle'),
                    component: Step6,
                },
                {
                    id: 7,
                    subtitle: 'Finish',
                    content: '',
                    component: Step8,
                },
            ],
        }
    },
    computed: {
        ...mapGetters('package', ['packageId', 'isEditMode', 'getStepValidation', 'loading', 'currentStep', 'savedStep']),
        sizes() {
            return {
                title: this.normalizeSize(32, true),
                stepTitle: '11px',
                subtitle: this.normalizeSize(13, true),
                subtitleMenu: this.normalizeSize(12, true),
                formStep: this.normalizeSize(15, true),
            }
        },
        visibleSteps() {
            // In edit mode, all steps are accessible after all validated
            // In create mode, limit visible steps based on the current 6-step flow
            return this.allSteps.slice(1, 6)
        },
        canEdit() {
            // Can only edit if all steps are validated
            return this.completedSteps.length >= 5
        },
    },
    created() {
        // Initialize store with props data
        this.setCountryVatRates(this.country_vat_rates)
        this.setAvailableActivities(this.prestations_with_prices_times)
        this.setAvailableTags(this.tags)

        // If editing, load package data and set completed steps
        if (this.id) {
            this.loadPackageData()
            // All steps are completed in edit mode
            this.completedSteps = [1, 2, 3, 4, 5]
            this.highestValidatedStep = 6
            this.step = 4 // Tarification — étape la plus utilisée en édition formule
        } else if (this.package_data && this.package_data.steps) {
            // Resume creation from saved step
            this.loadPackageData()
            this.step = this.package_data.steps === 1 ? 2 : this.package_data.steps
            this.updateCompletedSteps(this.step)
        } else {
            // Reset store for new package
            this.resetStore()
            this.highestValidatedStep = 2
        }
    },
    methods: {
        ...mapActions('package', [
            'setCountryVatRates',
            'setAvailableActivities',
            'setAvailableTags',
            'loadPackage',
            'savePackage',
            'resetStore',
            'setCurrentStep',
            'saveStepProgress',
        ]),
        normalizeSize,
        normalizeFontSize,
        _confirmLeave(onConfirm, onCancel) {
            this._pendingConfirm = onConfirm
            this._pendingCancel = onCancel || null
            this.showConfirmDialog = true
        },
        _onConfirmDialog() {
            this.showConfirmDialog = false
            if (typeof this._pendingConfirm === 'function') {
                this._pendingConfirm()
            }
            this._pendingConfirm = null
            this._pendingCancel = null
        },
        _onCancelDialog() {
            this.showConfirmDialog = false
            if (typeof this._pendingCancel === 'function') {
                this._pendingCancel()
            }
            this._pendingConfirm = null
            this._pendingCancel = null
        },

        getStepDisplayNumber(stepId) {
            // Map step id to display number (1-based, shifted by 1 because step 1 is hidden)
            return stepId - 1
        },

        updateCompletedSteps(currentStep) {
            this.completedSteps = []
            for (let i = 1; i < currentStep; i++) {
                this.completedSteps.push(i)
            }
            this.highestValidatedStep = currentStep
        },

        async loadPackageData() {
            if (this.package_data) {
                // Use pre-loaded data from blade
                this.$store.commit('package/LOAD_PACKAGE', this.package_data)
            } else if (this.id) {
                // Fetch from API
                await this.loadPackage(this.id)
            }
        },

        async handleNextStep(currentStepId) {
            // Validate current step before proceeding
            if (!this.getStepValidation(currentStepId)) {
                console.warn('Step validation failed for step:', currentStepId)
                this.$toast.error('Veuillez compléter tous les champs requis avant de continuer.')
                return
            }

            // Save progress to database
            const success = await this.saveCurrentProgress(currentStepId + 1)
            if (!success) {
                return
            }

            // Update URL with package ID after first creation (same as Experience)
            if (this.packageId && !this.id) {
                const newUrl = `/back-office/v3/package/create/${this.packageId}`
                window.history.replaceState({ packageId: this.packageId }, '', newUrl)
            }

            // Mark current step as completed
            if (!this.completedSteps.includes(currentStepId)) {
                this.completedSteps.push(currentStepId)
            }

            const nextStepId = currentStepId + 1
            this.goToStep(nextStepId)

            // Update highest validated step
            if (nextStepId > this.highestValidatedStep) {
                this.highestValidatedStep = nextStepId
            }
        },

        async saveCurrentProgress(stepNumber) {
            if (this.isSaving) return false

            this.isSaving = true
            try {
                // Save the current package state with step number
                await this.saveStepProgress(stepNumber)
                return true
            } catch (error) {
                this.$toast.error(error.response.data.message)
                if (error.response && (error.response.status === 422 || error.response.status === 500)) {
                    let message = 'Une erreur est survenue lors de la sauvegarde.'
                    if (error.response.data && error.response.data.errors) {
                        const firstErrorKey = Object.keys(error.response.data.errors)[0]
                        message = error.response.data.errors[firstErrorKey][0]
                    } else if (error.response.data && error.response.data.message) {
                        message = error.response.data.message
                    }
                    this.$toast.error(message)
                } else {
                    this.$toast.error('Une erreur est survenue lors de la sauvegarde.')
                }
                return false
            } finally {
                this.isSaving = false
            }
        },

        goToStep(nextStep) {
            this.executeStepChange(nextStep)
        },

        goToPrevious(previousStep) {
            if (previousStep < 1) return
            if (previousStep === 1) return
            if (this.hasAnyUnsavedChanges()) {
                this._confirmLeave(async () => {
                    await this._revertStoreChanges()
                    this.executeStepChange(previousStep)
                })
            } else {
                this.executeStepChange(previousStep)
            }
        },

        async handleStepClick(targetStep) {
            if (targetStep === 1) return
            if (targetStep === this.step) return

            const refName = 'step' + this.step
            const stepComponent = this.$refs[refName]
            const instance = Array.isArray(stepComponent) ? stepComponent[0] : stepComponent

            if (instance && typeof instance.hasChanges === 'function' && instance.hasChanges()) {
                const t = targetStep
                this._confirmLeave(async () => {
                    await this._revertStoreChanges()
                    if (!this.id && t > this.highestValidatedStep) {
                        this.$toast.info(this.$t('package.notifications.validationStepFirst'))
                        return
                    }
                    this.executeStepChange(t)
                })
                return
            }

            if (this.id) {
                this.executeStepChange(targetStep)
            } else if (targetStep <= this.highestValidatedStep) {
                this.executeStepChange(targetStep)
            } else {
                this.$toast.info(this.$t('package.notifications.validationStepFirst'))
            }
        },

        async _revertStoreChanges() {
            if (this.id) {
                await this.loadPackage(this.id)
            } else if (this.package_data) {
                this.$store.commit('package/LOAD_PACKAGE', this.package_data)
            } else if (this.packageId) {
                await this.loadPackage(this.packageId)
            } else {
                this.resetStore()
            }
        },

        executeStepChange(targetStep) {
            const previousStep = this.step
            this.step = targetStep
            this.setCurrentStep(targetStep)
            this.$nextTick(() => {
                const refName = 'step' + targetStep
                const stepComponent = this.$refs[refName]
                const instance = Array.isArray(stepComponent) ? stepComponent[0] : stepComponent
                if (instance && typeof instance.resetInitialState === 'function') {
                    instance.resetInitialState()
                }
                const prevRefName = 'step' + previousStep
                const prevComponent = this.$refs[prevRefName]
                const prevInstance = Array.isArray(prevComponent) ? prevComponent[0] : prevComponent
                if (prevInstance && typeof prevInstance.resetInitialState === 'function') {
                    prevInstance.resetInitialState()
                }
            })
        },

        async handleSaveAndExit() {
            const refName = 'step' + this.step
            const stepComponent = this.$refs[refName]

            if (!stepComponent) {
                console.warn(`⚠️ Ref ${refName} not found`)
                return
            }

            const instance = Array.isArray(stepComponent) ? stepComponent[0] : stepComponent

            // Call save method on step component if exists
            if (typeof instance.saveStep === 'function') {
                await instance.saveStep(true) // true = redirect after save
            } else {
                // Fallback: save via store and redirect
                try {
                    await this.saveCurrentProgress(this.step)
                    if (typeof instance.resetInitialState === 'function') {
                        instance.resetInitialState()
                    }
                    this.redirectToList()
                } catch (error) {
                    this.$toast.error(error.response.data.message)
                    console.error('Error saving package:', error)
                }
            }
        },

        hasAnyUnsavedChanges() {
            if (this._forceExit) return false
            const refName = 'step' + this.step
            const comp = this.$refs[refName]
            const instance = Array.isArray(comp) ? comp[0] : comp
            return !!(instance && typeof instance.hasChanges === 'function' && instance.hasChanges())
        },

        handleRequestNavigation(navEvent) {
            if (this.hasAnyUnsavedChanges()) {
                navEvent.defaultPrevented = true
            }
        },

        _handleForceNavigation() {
            if (this._beforeUnloadHandler) {
                window.removeEventListener('beforeunload', this._beforeUnloadHandler)
            }
            this._forceExit = true
        },

        redirectToList() {
            const baseUrl = `${window.location.protocol}//${window.location.host}`
            window.location.href = `${baseUrl}/back-office/v3/experience`
        },
    },
    mounted() {
        this._beforeUnloadHandler = (e) => {
            if (this.hasAnyUnsavedChanges()) {
                e.preventDefault()
                e.returnValue = ''
            }
        }
        window.addEventListener('beforeunload', this._beforeUnloadHandler)

        this._turboBeforeVisitHandler = (e) => {
            if (this.hasAnyUnsavedChanges()) {
                e.preventDefault()
                const url = e.detail.url
                this._confirmLeave(() => {
                    this._forceExit = true
                    if (this._beforeUnloadHandler) {
                        window.removeEventListener('beforeunload', this._beforeUnloadHandler)
                    }
                    if (window.Turbo) {
                        window.Turbo.visit(url)
                    } else {
                        window.location.href = url
                    }
                })
            }
        }
        document.addEventListener('turbo:before-visit', this._turboBeforeVisitHandler)

        EventBus.$on('request-navigation', this.handleRequestNavigation)
        EventBus.$on('force-navigation', this._handleForceNavigation)
    },
    beforeRouteLeave(to, from, next) {
        if (this.hasAnyUnsavedChanges()) {
            this._confirmLeave(
                () => {
                    this._forceExit = true
                    if (this._beforeUnloadHandler) {
                        window.removeEventListener('beforeunload', this._beforeUnloadHandler)
                    }
                    next()
                },
                () => {
                    next(false)
                }
            )
        } else {
            next()
        }
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this._beforeUnloadHandler)
        document.removeEventListener('turbo:before-visit', this._turboBeforeVisitHandler)
        EventBus.$off('request-navigation', this.handleRequestNavigation)
        EventBus.$off('force-navigation', this._handleForceNavigation)
    },
}
</script>

<style scoped>
.v-sheet.v-stepper:not(.v-sheet--outlined) {
    box-shadow: none;
    height: 100%;
}

.step_title_content {
    font-family: 'Poppins Light', sans-serif;
    text-align: center;
    margin-bottom: 0;
}

/* Stepper container */
.stepper-container {
    display: flex;
    flex-direction: column;
    position: relative;
    padding-left: 40px;
    border-right: 0.5px solid #5f5fa3;
}

/* Step item */
.step-item {
    position: relative;
    display: flex;
    align-items: flex-start;
    cursor: pointer;
    min-height: 80px;
}

.stepper-container-edit {
    background-color: rgba(0, 0, 0, 0.05);
    border-right: none !important;
    padding-left: 0 !important;
}

.dark .stepper-container-edit {
    background-color: #d9d9d90d;
}

/* Circle base */
.circle {
    position: relative;
    width: 18px;
    min-width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid #021832;
    background: transparent;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
}

.dark .circle {
    border: 2px solid #fff;
}

/* Ligne verticale */
.line {
    position: absolute;
    left: 10px;
    top: 30px;
    width: 1px;
    height: 40px;
    background: #021832;
    opacity: 0.3;
    z-index: 1;
}

.dark .line {
    background: #fff;
}

/* ✅ Étape terminée */
.step-item.completed .circle {
    background: #06b217;
    border-color: #06b217;
}

.step-item.completed .line {
    background: #06b217;
}

/* 🟣 Étape active */
.step-item.active .circle {
    border-color: #7d2dbd;
    box-shadow: 0 0 8px #7d2dbd;
}

.step-title {
    font-family: 'Roboto Medium';
    text-transform: uppercase;
}

.step-subtitle {
    font-family: 'Roboto Medium';
}

/* Étapes à venir (non actives) */
.step-item:not(.active):not(.completed) .step-title,
.step-item:not(.active):not(.completed) .step-subtitle,
.step-item:not(.active):not(.completed) .circle {
    opacity: 0.3;
}

.step-item.activeMenu {
    border-left: 3px solid;
    border-left-color: #7d2dbd;
    background-color: #1a1f371a;

    border-top-right-radius: 12px;
    border-bottom-right-radius: 12px;
    max-width: 80%;
}

.dark .step-item.activeMenu {
    background-color: rgba(255, 255, 255, 0.09);
}

.step-item.activeMenu *,
.step-item.completedMenu * {
    opacity: 1 !important;
}

.form-header {
    margin-bottom: 40px;
}

.form-step {
    text-transform: uppercase;
    margin-bottom: 6px;
}

/* use from create */
.step-item.disabled {
    pointer-events: none;
    cursor: not-allowed;
}

/* Fade transition for step changes */
.fade-step-enter-active,
.fade-step-leave-active {
    transition:
        opacity 0.25s ease,
        transform 0.25s ease;
}

.fade-step-enter {
    opacity: 0;
    transform: translateY(10px);
}

.fade-step-leave-to {
    opacity: 0;
    transform: translateY(-10px);
}
</style>
